import { GROUP, OPERATOR_AND, PAAS_ACTION } from 'constants/MeshbotConstant';

/**
 * Returns true if the block or any trigger within its subtree is a PAAS (Cloud Services) trigger.
 * GROUP objects are not themselves considered PAAS triggers — only leaf triggers are.
 *
 * @param {Object} block - A trigger or group object
 * @returns {boolean}
 */
const hasPaasInSubtree = (block) => {
    if (block.type !== GROUP && block.selectedFieldTrigger === PAAS_ACTION) {
        return true;
    }

    if (Array.isArray(block.blocks)) {
        return block.blocks.some(hasPaasInSubtree);
    }

    return false;
};

/**
 * Counts the number of PAAS (Cloud Services) sources among the direct children of a group.
 * A source is either:
 * - A non-group trigger with selectedFieldTrigger === PAAS_ACTION
 * - A child GROUP that has any PAAS trigger anywhere in its subtree
 *
 * This means an AND group with [direct_PAAS, childGroup(OR, [PAAS])] has 2 PAAS sources
 * and should be treated as a violation, because both paths require a Cloud Services webhook.
 *
 * @param {Array<Object>} blocks - Direct children of a group
 * @returns {number}
 */
const countPaasSources = (blocks) =>
    blocks.filter((block) =>
        block.type === GROUP ? hasPaasInSubtree(block) : block.selectedFieldTrigger === PAAS_ACTION,
    ).length;

/**
 * Returns true if `id` equals the given block's id or is found anywhere in its subtree.
 *
 * @param {Object} block
 * @param {string} id
 * @returns {boolean}
 */
const containsGroupId = (block, id) =>
    block.id === id || (Array.isArray(block.blocks) && block.blocks.some((b) => containsGroupId(b, id)));

/**
 * Returns true if `addingInsideGroupId` is inside a direct child of `blocks` that is already
 * a PAAS source (i.e. adding PAAS there does not create a new PAAS source for this group).
 *
 * @param {Array<Object>} blocks - Direct children of the AND group being evaluated
 * @param {string|null} addingInsideGroupId - ID of the group that directly contains the trigger
 * @returns {boolean}
 */
const isAddingInsideExistingPaasSource = (blocks, addingInsideGroupId) => {
    if (!addingInsideGroupId) {
        return false;
    }

    return blocks.some(
        (block) => block.type === GROUP && hasPaasInSubtree(block) && containsGroupId(block, addingInsideGroupId),
    );
};

/**
 * Recursively checks whether any AND-operator group in the trigger tree
 * contains a violation of the "at most one PAAS source per AND group" rule.
 *
 * A violation occurs when:
 * - An AND group already has ≥1 PAAS source and effectiveValue is PAAS_ACTION
 *   (user is trying to add another PAAS trigger to that group), unless the trigger
 *   is being added inside a child group that is already the counted PAAS source.
 * - The specific group being changed to AND already has >1 PAAS source.
 *   When targetGroupId is null every group is a candidate (backward-compatible);
 *   when targetGroupId is provided only that group triggers the check.
 *
 * @param {Array<Object>} blocks - Array of trigger or group objects to inspect
 * @param {string} effectiveValue - The resolved action value: either PAAS_ACTION or OPERATOR_AND
 * @param {string|null} [targetGroupId=null] - ID of the group whose operator is being changed to AND
 * @param {string|null} [addingInsideGroupId=null] - ID of the group directly containing the trigger being type-selected
 * @returns {boolean} True if a violation is found anywhere in the tree
 */
const hasAndPaasViolation = (blocks, effectiveValue, targetGroupId = null, addingInsideGroupId = null) =>
    blocks.some((block) => {
        if (block.type === GROUP && Array.isArray(block.blocks)) {
            const paasCount = countPaasSources(block.blocks);
            const isAndGroup = block.optionType === OPERATOR_AND;
            const isTarget = targetGroupId === null || block.id === targetGroupId;

            return (
                (isAndGroup &&
                    effectiveValue === PAAS_ACTION &&
                    paasCount >= 1 &&
                    !isAddingInsideExistingPaasSource(block.blocks, addingInsideGroupId)) ||
                (paasCount > 1 && effectiveValue === OPERATOR_AND && isTarget) ||
                hasAndPaasViolation(block.blocks, effectiveValue, targetGroupId, addingInsideGroupId)
            );
        }

        if (Array.isArray(block.blocks)) {
            return hasAndPaasViolation(block.blocks, effectiveValue, targetGroupId, addingInsideGroupId);
        }

        return false;
    });

/**
 * Returns true if the proposed action would violate the rule that an AND-operator
 * group cannot contain more than one Cloud Services (PAAS) source.
 *
 * A PAAS source is either a direct PAAS trigger or a child group that contains
 * any PAAS trigger in its subtree. This prevents scenarios like:
 *   AND[direct_PAAS, childOR[PAAS, PAAS]] — two Cloud Services paths under AND.
 *
 * Handles three call sites in MeshBotLocalForm:
 * - Selecting a trigger type:    (triggers, 'PAAS', globalOp, null, parentGroupId)  — user picks Cloud Services
 * - Changing a group's operator: (triggers, 'and',  globalOp, groupId)              — user sets per-item AND
 * - Changing the global operator:(triggers, '',     'and')                           — user sets top-level AND
 *
 * @param {Array<Object>} listRuleTriggers - Current list of trigger/group objects
 * @param {string} [value=''] - New value: trigger type (e.g. 'PAAS'), operator (e.g. 'and'), or ''
 * @param {string} [optionType=''] - Current or new global operator (e.g. 'and', 'or')
 * @param {string|null} [targetGroupId=null] - ID of the group whose operator is being changed to AND
 * @param {string|null} [addingInsideGroupId=null] - ID of the group directly containing the trigger being type-selected
 * @returns {boolean} True if the action should be blocked
 *
 * @example
 * // Block selecting a second PAAS trigger when AND is active
 * isMultipleCloudServiceWithAnd([{ selectedFieldTrigger: 'PAAS' }, {}], 'PAAS', 'and'); // true
 *
 * @example
 * // Allow selecting PAAS when operator is OR
 * isMultipleCloudServiceWithAnd([{ selectedFieldTrigger: 'PAAS' }, {}], 'PAAS', 'or'); // false
 *
 * @example
 * // Block switching global operator to AND when two PAAS triggers exist
 * isMultipleCloudServiceWithAnd([{ selectedFieldTrigger: 'PAAS' }, { selectedFieldTrigger: 'PAAS' }], '', 'and'); // true
 */
export const isMultipleCloudServiceWithAnd = (
    listRuleTriggers = [],
    value = '',
    optionType = '',
    targetGroupId = null,
    addingInsideGroupId = null,
) => {
    const effectiveValue = value || optionType;

    if (effectiveValue !== PAAS_ACTION && effectiveValue !== OPERATOR_AND) {
        return false;
    }

    const topLevel = { type: GROUP, optionType, blocks: listRuleTriggers };

    return hasAndPaasViolation([topLevel], effectiveValue, targetGroupId, addingInsideGroupId);
};
